// ══════════════════════════════════════════════════════════════
// COMPASS – Notification (In-App Feed) Effects
// ══════════════════════════════════════════════════════════════
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { NotificationActions } from './notification.actions';
import { ApiConfiguration } from '../../api/api-configuration';
import { history } from '../../api/fn/notifications/history';
import { poll } from '../../api/fn/notifications/poll';
import { errorMessage, readApiError } from '../../utils/api-error.utils';

const BELL_REFRESH_INTERVAL_MS = 30_000;

@Injectable()
export class NotificationEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);

  readonly startBackgroundRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.startBackgroundRefresh),
      switchMap(() =>
        timer(0, BELL_REFRESH_INTERVAL_MS).pipe(
          takeUntil(this.actions$.pipe(ofType(NotificationActions.stopBackgroundRefresh))),
          map(() => NotificationActions.refreshBell()),
        ),
      ),
    ),
  );

  readonly refreshBell$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.refreshBell),
      switchMap(() =>
        history(this.http, this.apiConfig.rootUrl).pipe(
          map((response) => NotificationActions.refreshBellSuccess({ items: response.body })),
          catchError((error) => [
            NotificationActions.refreshBellFailure({
              error: errorMessage(readApiError(error), 'NOTIFICATIONS.REFRESH_FAILED'),
            }),
          ]),
        ),
      ),
    ),
  );

  readonly openBellMenu$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationActions.openBellMenu),
      switchMap(() =>
        poll(this.http, this.apiConfig.rootUrl).pipe(
          switchMap((response) => [
            NotificationActions.pollSuccess({ items: response.body }),
            NotificationActions.refreshBell(),
          ]),
          catchError((error) => [
            NotificationActions.pollFailure({
              error: errorMessage(readApiError(error), 'NOTIFICATIONS.MARK_READ_FAILED'),
            }),
            NotificationActions.refreshBell(),
          ]),
        ),
      ),
    ),
  );
}
